/**
 * Double banner / editor smart links: structured { type, value } → storefront path.
 * Legacy: `kind` is accepted as alias for `type`; plain strings → custom.
 */

import { normalizeLink } from '@/lib/storefront/normalizeLink'

export type SmartLinkType = 'category' | 'product' | 'collection' | 'custom';

export type SmartLinkPayload = {
  type: SmartLinkType;
  value: string;
};

function isSmartLinkType(x: string): x is SmartLinkType {
  return x === 'category' || x === 'product' || x === 'collection' || x === 'custom';
}

/** Normalize editor / API / legacy saved shapes to { type, value }. */
export function normalizeSmartLinkPayload(raw: unknown): SmartLinkPayload | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const s = raw.trim();
    return s ? { type: 'custom', value: s } : null;
  }
  if (typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const rawType = String(o.type ?? o.kind ?? 'custom').toLowerCase();
  const type: SmartLinkType = isSmartLinkType(rawType) ? rawType : 'custom';
  const value = String(o.value ?? '').trim();
  return { type, value };
}

/**
 * Returns a path suitable for `getStorePath()` / router (relative), or full URL for custom https.
 * Accepts legacy `{ type, value }` objects via {@link normalizeLink}.
 */
export function resolveSmartLink(link: unknown): string {
  const s = normalizeLink(link).trim()
  if (!s) return '#'
  if (/^https?:\/\//i.test(s)) return s
  return s.startsWith('/') ? s : `/${s}`
}
